import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

export interface CrmFilters {
  customer_type?: 'individual' | 'corporate' | '';
  search_word?: string;
  [key: string]: any;
}

export const DEFAULT_CRM_FILTERS: CrmFilters = {
  customer_type: '',
  search_word: '',
};

export interface SortConfig {
  field: string;
  order: 'asc' | 'desc';
}

const GET_CUSTOMERS_URL = ep('get_crm_customers');

export const useCrmTable = (
  initialFilters: CrmFilters = DEFAULT_CRM_FILTERS,
  initialSort: SortConfig = { field: 'updated_at', order: 'desc' }
) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<CrmFilters>(initialFilters);
  const [sort, setSort] = useState<SortConfig>(initialSort);
  
  // 競合状態（Race Condition）を防ぐためのリクエスト管理用 ref
  const lastRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++lastRequestId.current;
    setLoading(true);
    setData([]); // 新しいリクエスト開始時にデータをクリア

    try {
      const response = await axios.post(GET_CUSTOMERS_URL, {
        page,
        pageSize,
        filters,
        sortField: sort.field,
        sortOrder: sort.order,
      });

      // 最新のリクエストである場合のみ結果を反映
      if (requestId === lastRequestId.current && response.data.status === 'success') {
        setData(response.data.rowsResult || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      if (requestId === lastRequestId.current) {
        console.error('Error fetching CRM data:', error);
      }
    } finally {
      if (requestId === lastRequestId.current) {
        setLoading(false);
      }
    }
  }, [page, pageSize, filters, sort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 状態更新時に即座にローディング表示へ移行するためのラップ関数群
  const updateFilters = useCallback((newFilters: CrmFilters) => {
    setLoading(true);
    setData([]);
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSort = useCallback((field: string) => {
    setLoading(true);
    setData([]);
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
    setPage(0);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setLoading(true);
    setData([]);
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setLoading(true);
    setData([]);
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    sort,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    updateFilters,
    handleSort,
    refresh: fetchData
  };
};
